import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Content, Section, TextDiv } from "../../styles/Common";
import { styled } from "styled-components";
import { ReactComponent as IcoDropDown } from "../../styles/images/ico_arrow_drowdown.svg";
import OnePageLayout from "../../components/layout/OnePageLayout";
import TopPointBanner from "../../components/main/TopPointBanner";
import { getToken, returnLoginStatus } from "../../components/hooks/authFunc";
import { useDispatch, useSelector } from "react-redux";
import {
  returnOptionText,
  returnYYYYMMDD,
} from "../../components/hooks/basicFunc";
import { userActions } from "../../store/slices/userSlice";
import PointHistory from "../../components/myPage/PointHistory";
import ExtinctionHistory from "../../components/myPage/ExtinctionHistory";
import AmoHistoryComponent from "../../components/amo/AmoHistoryComponent";
import { amoActions } from "../../store/slices/amoSlice";
import OnlyHeaderLayout from "../../components/layout/OnlyHeaderLayout";

const AmoHistory = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const isLogin = returnLoginStatus();
  const [option, setOption] = useState(0);
  const accessToken = getToken();
  const historyData = useSelector((state) => state.amo.amoHistory);
  const [data, setData] = useState([]);
  const [pageSize, setPageSize] = useState(50);

  const today = new Date();
  const year = today.getFullYear(); // 년
  const month = today.getMonth(); // 월
  const day = today.getDate(); // 일
  // 일주일 전 구하기
  const weekAgoDay = new Date(year, month, day - 7);
  // 한달 전 구하기
  const monthAgoDay = new Date(year, month - 1, day);
  // 3개월 전 구하기
  const threeMonthAgoDay = new Date(year, month - 3, day);
  // 6개월 전 구하기
  const sixMonthAgoDay = new Date(year, month - 6, day);
  // 일년 전 구하기
  const yearAgoDay = new Date(year - 1, month, day);
  const [dayStart, setDayStart] = useState(weekAgoDay); //최초진입 시, 현재로부터 일주일전.
  const [dayEnd, setDayEnd] = useState(today); //최초진입 시, 당일.

  const urls = process.env.REACT_APP_BASE_URL;

  const getData = () => {
    const params = {
      page: 1, // (optioinal) Page number, default 1
      pageSize: pageSize, // (optioinal) Max 50
      type: 4, // (optioinal) 4 아모스왑
      dayStart: returnYYYYMMDD(dayStart), // (optional) YYYY-MM-DD
      dayEnd: returnYYYYMMDD(dayEnd), // (optional) YYYY-MM-DD
    };
    dispatch(amoActions.fetchAmoHistory({ params, accessToken }));
  };

  const settingStartDay = (days) => {
    switch (days) {
      case "week":
        setDayStart(weekAgoDay);
        setDayEnd(today);
        break;
      case "month":
        setDayStart(monthAgoDay);
        setDayEnd(today);
        break;
      case "threeMonth":
        setDayStart(threeMonthAgoDay);
        setDayEnd(today);
        break;
      case "sixMonth":
        setDayStart(sixMonthAgoDay);
        setDayEnd(today);
        break;
      case "year":
        setDayStart(yearAgoDay);
        setDayEnd(today);
        break;
      default:
        setDayStart(today);
        setDayEnd(today);
    }
  };

  useEffect(() => {
    getData();
  }, [dayStart, dayEnd]);

  function getNewData(page, url) {
    const params = {
      page: page + 1, // (optioinal) Page number, default 1
      pageSize: pageSize, // (optioinal) Max 50
      type: 4, // (optioinal) 4 아모스왑
      dayStart: returnYYYYMMDD(dayStart), // (optional) YYYY-MM-DD
      dayEnd: returnYYYYMMDD(dayEnd), // (optional) YYYY-MM-DD
    };
    const options = {
      method: "POST",
      body: JSON.stringify({
        params,
        accessToken: accessToken,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    };
    return fetch(url, options)
      .then((response) => response.json())
      .then((data) => {
        setData((prevData) => [...prevData, data.params]);
      });
  }

  useEffect(() => {
    let page;
    if (Object.keys(historyData).length !== 0 && historyData.count != 0) {
      setData([historyData]);
      page = 1;

      const handleScroll = () => {
        // 현재 스크롤 위치
        const scrollY = window.scrollY || window.pageYOffset;
        const viewportHeight = window.innerHeight;
        const documentHeight = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.offsetHeight,
          document.body.clientHeight,
          document.documentElement.clientHeight
        );

        const distanceToBottom = documentHeight - (viewportHeight + scrollY);
        const totalPage = Math.ceil(historyData.count / pageSize);
        //전체수에서 페이지사이즈를 나누면 totalPage
        //현재페이지는 totalPage를 넘으면 안됨.
        if (distanceToBottom <= 1 && totalPage > page) {
          // console.log("화면 바닥에 닿았습니다! 목록이 더 있습니다", page);
          getNewData(page, `${urls}/mu/user/amo/history`);
          page++;
        }
      };

      // 스크롤 이벤트 리스너 등록
      window.addEventListener("scroll", handleScroll);

      // 컴포넌트가 언마운트될 때 이벤트 리스너 제거
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    } else {
      setData([]);
    }
  }, [historyData]); // useEffect의 두 번째 매개변수로 빈 배열을 전달하여 한 번만 실행되도록 설정

  // Custom header component
  const CustomHeader = ({ date, changeYear, changeMonth }) => (
    <div>
      <button onClick={() => changeYear(-1)}>Previous Year</button>
      <button onClick={() => changeMonth(-1)}>Previous Month</button>
      <h2>
        {date.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
      </h2>
      <button onClick={() => changeMonth(1)}>Next Month</button>
      <button onClick={() => changeYear(1)}>Next Year</button>
    </div>
  );

  return (
    <OnlyHeaderLayout title="AMO 거래내역">
      <Section className="nonePaddingTop">
        <div>
          {isLogin && <TopPointBanner />}
          <Content>
            <div>
              <DatePickerWrap>
                <Period>
                  <li
                    className="pointer"
                    onClick={() => {
                      settingStartDay("week");
                    }}
                  >
                    1주일
                  </li>
                  <li
                    className="pointer"
                    onClick={() => {
                      settingStartDay("month");
                    }}
                  >
                    1개월
                  </li>
                  <li
                    className="pointer"
                    onClick={() => {
                      settingStartDay("threeMonth");
                    }}
                  >
                    3개월
                  </li>
                  <li
                    className="pointer"
                    onClick={() => {
                      settingStartDay("sixMonth");
                    }}
                  >
                    6개월
                  </li>
                  <li
                    className="pointer"
                    onClick={() => {
                      settingStartDay("year");
                    }}
                  >
                    1년
                  </li>
                </Period>
                <Duration>
                  <ExtinctionPickDate>
                    <div>
                      {" "}
                      <input
                        className="pointer date-picket-input"
                        type="date"
                        onChange={(e) => {
                          setDayStart(e.target.value);
                        }}
                        value={returnYYYYMMDD(dayStart) || new Date()}
                      />
                    </div>
                    -
                    <div>
                      <input
                        className="pointer date-picket-input"
                        type="date"
                        onChange={(e) => {
                          setDayEnd(e.target.value);
                        }}
                        value={returnYYYYMMDD(dayEnd) || new Date()}
                      />
                    </div>
                  </ExtinctionPickDate>
                </Duration>
              </DatePickerWrap>
              <Desc>
                <AmoHistoryComponent data={data} />
              </Desc>
            </div>
          </Content>
        </div>
      </Section>
    </OnlyHeaderLayout>
  );
};

export default AmoHistory;

const DatePickerWrap = styled.div`
  .date-picket-input {
    text-align: center;
    font-family: NanumSquareR;
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 4rem;
    position: relative;
  }
`;
const Period = styled.ul`
  margin-top: 2rem;
  background-color: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.black};
  display: flex;
  li {
    width: 25%;
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 4rem;
    text-align: center;
    position: relative;
    &::before {
      display: block;
      content: "";
      width: 0.9px;
      height: 2.3rem;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      margin: auto;
      background-color: #d3d3d3;
    }
    &:nth-of-type(1) {
      &::before {
        display: none;
      }
    }
  }
`;
const Duration = styled.div`
  margin-top: 1rem;
  position: relative;
  display: flex;
  justify-content: space-between;
`;
const PickDate = styled.div`
  width: calc(100% - 9.5rem);
  display: flex;
  justify-content: space-between;
  line-height: 4rem;
  & > div {
    border: 1px solid #000;
    width: calc(50% - 0.5rem);
    height: 4rem;
    text-align: center;
    background-color: ${({ theme }) => theme.color.white};
    font-size: 1.2rem;
    font-weight: 700;
  }
  input {
    position: relative;
    width: 100%;
    height: 100%;
    background-color: transparent;
    font-size: 1.2rem;
    font-weight: 600;
    color: ${({ theme }) => theme.color.black};
  }
  input[type="date"]::-webkit-calendar-picker-indicator {
    position: absolute; // 이를 설정하기 위해 사전에 relative를 설정한 것이다.
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: transparent; // 배경은 투명하게,
    color: transparent; // 글자도 투명하게! 이 두 설정을 통해 캘린더 아이콘을 사라지게 만든다.
    cursor: pointer;
  }
  input::-webkit-date-and-time-value {
    text-align: center;
  }
  input[type="date"]::-webkit-inner-spin-button {
    display: none;
    appearance: none;
  }

  input[type="date"] {
    /* 달력을 열 수 있는 아이콘을 숨깁니다. */
    text-align: center;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    -ms-appearance: none;
    appearance: none;
    display: flex;
    justify-content: center;
  }
  @media screen and (-webkit-min-device-pixel-ratio: 0) {
    /* 안드로이드 기반 브라우저 */
    input[type="date"] {
      text-align: center;
    }
  }
`;

const ExtinctionPickDate = styled(PickDate)`
  width: 100%;
`;
const Desc = styled.div`
  margin-top: 1.5rem;
`;
